import { Textarea, Text, Box, Group } from '@mantine/core'
import { IconLock } from '@tabler/icons-react'
import { checkInputQuality } from '../../lib/inputQuality'

/**
 * Free-text response area for input beats.
 *
 * Two sizing modes:
 *  - `fill` (default): the textarea grows to fill its parent's height. Used by
 *    the legacy DialoguePanel, which gives it a flex column to fill.
 *  - `fill={false}`: the textarea autosizes between minRows/maxRows. Used by the
 *    new interface (GameScreen2 / ResponseDock), where the submit button stacks
 *    below it rather than the panel owning all the height.
 *
 * Colors track the active theme via --bw-* vars rather than hardcoded cyan, so
 * the field reskins with the story theme and high-contrast mode.
 *
 * Accessibility: the active field carries an aria-label (the beat prompt is also
 * shown on screen as the Story heading) and a description hint that Mantine wires
 * to the input via aria-describedby. When `locked`, the field is read-only with a
 * lock label so a revisited answer reads as fixed.
 *
 * @param {boolean} [fill=true] - Fill parent height (legacy) vs. autosize (new UI).
 * @param {string} [ariaLabel] - Accessible name for the textarea.
 */
export function InputPanel({
  value,
  onChange,
  onSubmit,
  locked = false,
  fill = true,
  ariaLabel = 'Your response',
}) {
  const handleKeyDown = (e) => {
    if (locked) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit()
    }
  }

  const qualityHint = !locked && checkInputQuality(value)
  // Hint shown via Mantine's `description` (auto aria-describedby) for active
  // beats. Locked beats use a custom lock row below instead.
  const activeHint = qualityHint
    ? 'Characters respond best to detailed input. Press Enter to submit, Shift+Enter for a new line.'
    : 'Press Enter to submit, Shift+Enter for a new line.'

  // Theme-aware field styling shared by both modes.
  const inputStyle = {
    background: locked
      ? 'color-mix(in srgb, var(--bw-panel-bg) 70%, transparent)'
      : 'var(--bw-panel-bg)',
    border: `1px solid ${locked
      ? 'color-mix(in srgb, var(--bw-accent) 25%, transparent)'
      : 'color-mix(in srgb, var(--bw-accent) 45%, transparent)'}`,
    color: locked ? 'color-mix(in srgb, var(--bw-body-color) 70%, transparent)' : 'var(--bw-body-color)',
    fontFamily: 'var(--bw-body-font)',
    fontSize: 'calc(1.0625rem * var(--bw-content-scale, 1))',
    lineHeight: 1.6,
    padding: '14px 16px',
    cursor: locked ? 'default' : 'text',
  }

  const sizingProps = fill
    ? {
        styles: {
          root: { flex: 1, display: 'flex', flexDirection: 'column' },
          wrapper: { flex: 1, display: 'flex', flexDirection: 'column' },
          input: { ...inputStyle, flex: 1, resize: 'none' },
        },
      }
    : {
        autosize: true,
        minRows: 5,
        maxRows: 12,
        styles: {
          input: inputStyle,
          description: { fontStyle: 'italic', opacity: 0.7, fontFamily: 'var(--bw-body-font)' },
        },
      }

  return (
    <Box style={fill ? { flex: 1, display: 'flex', flexDirection: 'column' } : undefined}>
      <Textarea
        aria-label={ariaLabel}
        placeholder={locked ? '' : 'Type your response…'}
        description={locked ? undefined : activeHint}
        inputWrapperOrder={['label', 'input', 'description', 'error']}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        readOnly={locked}
        {...sizingProps}
      />
      {locked && (
        <Group gap={6} mt={6} style={{ opacity: 0.6 }}>
          <IconLock size={13} style={{ color: 'var(--bw-accent)', flexShrink: 0 }} />
          <Text size="xs" c="dimmed" fs="italic" ff="var(--bw-body-font)">
            Your earlier response — locked for this playthrough
          </Text>
        </Group>
      )}
    </Box>
  )
}
